import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatINRFull, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, Search, FileText, Edit2, Trash2, ChevronDown, ChevronRight, Download, Eye, Package, Receipt } from 'lucide-react';

const PO_TYPES = ['SUPPLY', 'SERVICE'];
const PO_STATUSES = ['RECEIVED', 'ACTIVE', 'COMPLETED'];
const INVOICE_TYPES = ['SUPPLY', 'SERVICE', 'ADVANCE', 'RETENTION'];
const INVOICE_STATUSES = ['DRAFT', 'SUBMITTED', 'ACKNOWLEDGED', 'PAYMENT_RECEIVED'];

export default function POInvoicePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('purchase-orders');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [boqItems, setBoqItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [poTypeFilter, setPOTypeFilter] = useState('');
  const [poStatusFilter, setPOStatusFilter] = useState('');
  const [invTypeFilter, setInvTypeFilter] = useState('');
  const [invStatusFilter, setInvStatusFilter] = useState('');
  const [showCreatePO, setShowCreatePO] = useState(false);
  const [showCreateInv, setShowCreateInv] = useState(false);
  const [editPO, setEditPO] = useState(null);
  const [editInv, setEditInv] = useState(null);
  const [expandedInv, setExpandedInv] = useState(null);

  const fetchData = async () => {
    try {
      const [poRes, invRes, projRes] = await Promise.all([
        api.get('/po-invoices/purchase-orders'),
        api.get('/po-invoices/invoices'),
        api.get('/projects')
      ]);
      setPurchaseOrders(poRes.data.purchase_orders || []);
      setInvoices(invRes.data.invoices || []);
      setProjects(projRes.data.projects || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const canCreate = ['DIRECTOR', 'ACCOUNTANT'].includes(user?.role);

  const deletePO = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    await api.delete(`/po-invoices/purchase-orders/${id}`);
    fetchData();
  };

  const deleteInv = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await api.delete(`/po-invoices/invoices/${id}`);
    fetchData();
  };

  const filteredPOs = useMemo(() => {
    let items = purchaseOrders;
    if (poTypeFilter) items = items.filter(p => p.po_type === poTypeFilter);
    if (poStatusFilter) items = items.filter(p => p.status === poStatusFilter);
    return items;
  }, [purchaseOrders, poTypeFilter, poStatusFilter]);

  const filteredInvs = useMemo(() => {
    let items = invoices;
    if (invTypeFilter) items = items.filter(i => i.invoice_type === invTypeFilter);
    if (invStatusFilter) items = items.filter(i => i.status === invStatusFilter);
    return items;
  }, [invoices, invTypeFilter, invStatusFilter]);

  const [reportData, setReportData] = useState(null);

  const fetchReportData = async () => {
    try {
      const r = await api.get('/reports/po-invoice-report');
      setReportData(r.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (activeTab === 'receivables' || activeTab === 'tds-register') fetchReportData(); }, [activeTab]);

  const tabs = [
    { id: 'purchase-orders', label: 'Purchase Orders', icon: Package, count: purchaseOrders.length },
    { id: 'invoices', label: 'Invoices', icon: Receipt, count: invoices.length },
    { id: 'receivables', label: 'Receivables Ageing', icon: FileText },
    { id: 'tds-register', label: 'TDS Register', icon: FileText },
  ];

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="po-invoice-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">PO & Invoices</h1>
          <p className="text-sm text-slate-500">{purchaseOrders.length} POs, {invoices.length} Invoices</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <button onClick={() => setShowCreatePO(true)} data-testid="create-po-btn"
              className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
              <Plus size={16} /> New PO
            </button>
            <button onClick={() => setShowCreateInv(true)} data-testid="create-invoice-btn"
              className="bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-emerald-700 flex items-center gap-2">
              <Plus size={16} /> New Invoice
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              activeTab === t.id ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon size={14} /> {t.label} <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded-full ml-1">{t.count}</span>
          </button>
        ))}
      </div>

      {activeTab === 'purchase-orders' && (
        <>
          <div className="flex flex-wrap gap-3">
            <select value={poTypeFilter} onChange={e => setPOTypeFilter(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-po-type">
              <option value="">All Types</option>
              {PO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={poStatusFilter} onChange={e => setPOStatusFilter(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-po-status">
              <option value="">All Statuses</option>
              {PO_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">PO Number</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Vendor</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">PO Value</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Total (incl GST)</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                <th className="w-20"></th>
              </tr></thead>
              <tbody>
                {filteredPOs.map(po => (
                  <tr key={po.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`po-row-${po.id}`}>
                    <td className="py-2.5 px-4 font-mono text-xs text-[#1B5486] font-medium">{po.po_number}</td>
                    <td className="py-2.5 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${po.po_type === 'SUPPLY' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{po.po_type}</span></td>
                    <td className="py-2.5 px-4 text-xs">{po.project_code || '-'}</td>
                    <td className="py-2.5 px-4">{po.vendor_name || '-'}</td>
                    <td className="py-2.5 px-4 text-slate-500">{formatDate(po.po_date)}</td>
                    <td className="py-2.5 px-4 text-right font-mono">{formatINRFull(po.po_value)}</td>
                    <td className="py-2.5 px-4 text-right font-mono font-medium">{formatINRFull(po.total_amount)}</td>
                    <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(po.status)}`}>{formatStatus(po.status)}</span></td>
                    <td className="py-2.5 px-2 flex gap-1">
                      {canCreate && <button onClick={() => setEditPO({...po})} className="text-slate-400 hover:text-[#1B5486]" data-testid={`edit-po-${po.id}`}><Edit2 size={14} /></button>}
                      {user?.role === 'DIRECTOR' && <button onClick={() => deletePO(po.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filteredPOs.length === 0 && <div className="text-center py-12 text-slate-400">No purchase orders found</div>}
          </div>
        </>
      )}

      {activeTab === 'invoices' && (
        <>
          <div className="flex flex-wrap gap-3">
            <select value={invTypeFilter} onChange={e => setInvTypeFilter(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-inv-type">
              <option value="">All Types</option>
              {INVOICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={invStatusFilter} onChange={e => setInvStatusFilter(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-inv-status">
              <option value="">All Statuses</option>
              {INVOICE_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                <th className="w-8"></th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Invoice #</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">PO #</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">TDS</th>
                <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Net Receivable</th>
                <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                <th className="w-20"></th>
              </tr></thead>
              <tbody>
                {filteredInvs.map(inv => (
                  <React.Fragment key={inv.id}>
                    <tr className="border-b border-slate-50 hover:bg-slate-50" data-testid={`inv-row-${inv.id}`}>
                      <td className="py-2.5 px-2">
                        {(inv.line_items || []).length > 0 && (
                          <button onClick={() => setExpandedInv(expandedInv === inv.id ? null : inv.id)} className="text-slate-400 hover:text-slate-600">
                            {expandedInv === inv.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        )}
                      </td>
                      <td className="py-2.5 px-4 font-mono text-xs text-emerald-700 font-medium">{inv.invoice_number}</td>
                      <td className="py-2.5 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${
                        inv.invoice_type === 'SUPPLY' ? 'bg-blue-100 text-blue-700' :
                        inv.invoice_type === 'SERVICE' ? 'bg-purple-100 text-purple-700' :
                        inv.invoice_type === 'ADVANCE' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                      }`}>{inv.invoice_type}</span></td>
                      <td className="py-2.5 px-4 text-xs">{inv.project_code || '-'}</td>
                      <td className="py-2.5 px-4 text-xs font-mono">{inv.po_number || '-'}</td>
                      <td className="py-2.5 px-4 text-slate-500">{formatDate(inv.invoice_date)}</td>
                      <td className="py-2.5 px-4 text-right font-mono font-medium">{formatINRFull(inv.total_amount)}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-red-500">{inv.tds_deducted ? formatINRFull(inv.tds_deducted) : '-'}</td>
                      <td className="py-2.5 px-4 text-right font-mono text-emerald-600 font-medium">{formatINRFull(inv.net_receivable || (inv.total_amount - (inv.tds_deducted || 0)))}</td>
                      <td className="py-2.5 px-4"><InvoiceStatusBadge status={inv.status} /></td>
                      <td className="py-2.5 px-2 flex gap-1">
                        {canCreate && <button onClick={() => setEditInv({...inv})} className="text-slate-400 hover:text-emerald-600" data-testid={`edit-inv-${inv.id}`}><Edit2 size={14} /></button>}
                        {user?.role === 'DIRECTOR' && <button onClick={() => deleteInv(inv.id)} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>}
                      </td>
                    </tr>
                    {expandedInv === inv.id && (inv.line_items || []).length > 0 && (
                      <tr><td colSpan={11} className="bg-slate-50 px-8 py-3">
                        <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Line Items</p>
                        <table className="w-full text-xs">
                          <thead><tr className="text-slate-500">
                            <th className="text-left py-1 px-2">Description</th>
                            <th className="text-right py-1 px-2">Qty</th>
                            <th className="text-right py-1 px-2">Unit Price</th>
                            <th className="text-right py-1 px-2">Amount</th>
                          </tr></thead>
                          <tbody>
                            {inv.line_items.map((li, idx) => (
                              <tr key={idx} className="border-t border-slate-100">
                                <td className="py-1 px-2">{li.description || li.boq_item_id || '-'}</td>
                                <td className="py-1 px-2 text-right font-mono">{li.quantity}</td>
                                <td className="py-1 px-2 text-right font-mono">{formatINRFull(li.unit_price)}</td>
                                <td className="py-1 px-2 text-right font-mono font-medium">{formatINRFull(li.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>
            {filteredInvs.length === 0 && <div className="text-center py-12 text-slate-400">No invoices found</div>}
          </div>
        </>
      )}

      {activeTab === 'receivables' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="receivables-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">Receivables Ageing</h3>
          {reportData ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total Invoiced</p>
                  <p className="text-lg font-semibold text-slate-900 font-mono">{formatINRFull(reportData.summary?.total_invoiced)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600 mb-1">Received</p>
                  <p className="text-lg font-semibold text-green-700 font-mono">{formatINRFull(reportData.summary?.total_received)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-red-600 mb-1">TDS Deducted</p>
                  <p className="text-lg font-semibold text-red-700 font-mono">{formatINRFull(reportData.summary?.total_tds)}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-amber-600 mb-1">Outstanding</p>
                  <p className="text-lg font-semibold text-amber-700 font-mono">{formatINRFull(reportData.summary?.total_outstanding)}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600 mb-1">Invoice Count</p>
                  <p className="text-lg font-semibold text-blue-700">{reportData.summary?.invoice_count}</p>
                </div>
              </div>
              <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3">Ageing Buckets</h4>
              <div className="grid grid-cols-4 gap-3">
                {[['0-30 days', reportData.receivables_ageing?.['0_30'], 'bg-green-50 text-green-700'],
                  ['30-60 days', reportData.receivables_ageing?.['30_60'], 'bg-amber-50 text-amber-700'],
                  ['60-90 days', reportData.receivables_ageing?.['60_90'], 'bg-orange-50 text-orange-700'],
                  ['90+ days', reportData.receivables_ageing?.['90_plus'], 'bg-red-50 text-red-700']].map(([label, val, cls]) => (
                  <div key={label} className={`rounded-lg p-4 text-center ${cls}`}>
                    <p className="text-xs mb-1">{label}</p>
                    <p className="text-lg font-semibold font-mono">{formatINRFull(val)}</p>
                  </div>
                ))}
              </div>
            </>
          ) : <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>}
        </div>
      )}

      {activeTab === 'tds-register' && (
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="tds-register-tab">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">TDS Register</h3>
          {reportData ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice #</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Project</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice Amount</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">TDS Deducted</th>
                </tr></thead>
                <tbody>
                  {(reportData.tds_register || []).map((entry, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2 px-3 font-medium text-[#1B5486]">{entry.invoice_number}</td>
                      <td className="py-2 px-3 text-slate-500">{formatDate(entry.invoice_date)}</td>
                      <td className="py-2 px-3 text-slate-700">{entry.project_id}</td>
                      <td className="py-2 px-3 text-right font-mono">{formatINRFull(entry.total_amount)}</td>
                      <td className="py-2 px-3 text-right font-mono text-red-600 font-medium">{formatINRFull(entry.tds_deducted)}</td>
                    </tr>
                  ))}
                  {(reportData.tds_register || []).length === 0 && (
                    <tr><td colSpan={5} className="text-center py-8 text-slate-400">No TDS deductions recorded</td></tr>
                  )}
                </tbody>
              </table>
              {(reportData.tds_register || []).length > 0 && (
                <div className="flex justify-end mt-3 pt-3 border-t border-slate-200">
                  <span className="text-sm font-semibold text-slate-900">Total TDS: <span className="font-mono text-red-600">{formatINRFull(reportData.summary?.total_tds)}</span></span>
                </div>
              )}
            </div>
          ) : <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>}
        </div>
      )}

      {showCreatePO && <CreatePOModal projects={projects} onClose={() => setShowCreatePO(false)} onCreated={() => { setShowCreatePO(false); fetchData(); }} />}
      {showCreateInv && <CreateInvoiceModal projects={projects} purchaseOrders={purchaseOrders} onClose={() => setShowCreateInv(false)} onCreated={() => { setShowCreateInv(false); fetchData(); }} />}
      {editPO && <EditPOModal po={editPO} onClose={() => setEditPO(null)} onSaved={() => { setEditPO(null); fetchData(); }} />}
      {editInv && <EditInvoiceModal invoice={editInv} purchaseOrders={purchaseOrders} projects={projects} onClose={() => setEditInv(null)} onSaved={() => { setEditInv(null); fetchData(); }} />}
    </div>
  );
}

function InvoiceStatusBadge({ status }) {
  const colors = {
    DRAFT: 'bg-slate-100 text-slate-600',
    SUBMITTED: 'bg-blue-100 text-blue-700',
    ACKNOWLEDGED: 'bg-amber-100 text-amber-700',
    PAYMENT_RECEIVED: 'bg-green-100 text-green-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[status] || 'bg-slate-100 text-slate-600'}`}>{formatStatus(status)}</span>;
}

function CreatePOModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({
    po_number: '', po_type: 'SUPPLY', project_id: '', vendor_name: '', po_date: new Date().toISOString().slice(0, 10),
    po_value: '', gst_percent: '18', remarks: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/po-invoices/purchase-orders', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const gstAmt = (parseFloat(form.po_value || 0) * parseFloat(form.gst_percent || 0) / 100);
  const totalAmt = parseFloat(form.po_value || 0) + gstAmt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-po-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Purchase Order</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">PO Number *</label>
            <input value={form.po_number} onChange={e => setForm({...form, po_number: e.target.value})} required placeholder="e.g. SPO-2604-001" className="input-field w-full font-mono" data-testid="po-number-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PO Type *</label>
              <select value={form.po_type} onChange={e => setForm({...form, po_type: e.target.value})} className="input-field w-full" data-testid="po-type-select">
                {PO_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Project *</label>
              <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="input-field w-full" required data-testid="po-project-select">
                <option value="">Select</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Vendor Name *</label>
              <input value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} required className="input-field w-full" data-testid="po-vendor-name" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PO Date</label>
              <input type="date" value={form.po_date} onChange={e => setForm({...form, po_date: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PO Value *</label>
              <input type="number" value={form.po_value} onChange={e => setForm({...form, po_value: e.target.value})} required className="input-field w-full" data-testid="po-value-input" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">GST %</label>
              <select value={form.gst_percent} onChange={e => setForm({...form, gst_percent: e.target.value})} className="input-field w-full">
                {[0, 5, 12, 18, 28].map(g => <option key={g} value={g}>{g}%</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Total</label>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm font-mono font-medium text-emerald-700">{formatINRFull(totalAmt)}</div></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-po-btn" className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Creating...' : 'Create PO'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateInvoiceModal({ projects, purchaseOrders, onClose, onCreated }) {
  const [form, setForm] = useState({
    invoice_number: '', project_id: '', purchase_order_id: '', invoice_date: new Date().toISOString().slice(0, 10),
    invoice_type: 'SUPPLY', base_amount: '', gst_amount: '0', tds_deducted: '0', notes: ''
  });
  const [lineItems, setLineItems] = useState([{ boq_item_id: '', description: '', quantity: '', unit_price: '' }]);
  const [boqOptions, setBoqOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}/boq`).then(r => setBoqOptions(r.data.items || [])).catch(() => setBoqOptions([]));
    } else { setBoqOptions([]); }
  }, [form.project_id]);

  const projectPOs = purchaseOrders.filter(p => p.project_id === form.project_id);

  const addLineItem = () => setLineItems([...lineItems, { boq_item_id: '', description: '', quantity: '', unit_price: '' }]);
  const removeLineItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));
  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'boq_item_id' && value) {
      const boq = boqOptions.find(b => b.id === value);
      if (boq) {
        updated[idx].description = boq.description || boq.item_name || '';
        updated[idx].unit_price = boq.quoted_unit_price || boq.unit_cost_client || '';
        updated[idx].quantity = boq.quoted_qty || boq.units || '';
      }
    }
    setLineItems(updated);
  };

  const lineTotal = lineItems.reduce((sum, li) => sum + (parseFloat(li.quantity || 0) * parseFloat(li.unit_price || 0)), 0);
  const baseAmt = parseFloat(form.base_amount || 0) || lineTotal;
  const gstAmt = parseFloat(form.gst_amount || 0);
  const totalAmt = baseAmt + gstAmt;
  const tds = parseFloat(form.tds_deducted || 0);
  const netReceivable = totalAmt - tds;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const validLines = lineItems.filter(li => li.description || li.boq_item_id);
    try {
      await api.post('/po-invoices/invoices', {
        ...form,
        base_amount: baseAmt,
        line_items: validLines.map(li => ({
          boq_item_id: li.boq_item_id || '',
          description: li.description,
          quantity: parseFloat(li.quantity || 0),
          unit_price: parseFloat(li.unit_price || 0)
        }))
      });
      onCreated();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-invoice-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Invoice</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Invoice Number *</label>
            <input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} required placeholder="e.g. INV-2604-001 or Tally number" className="input-field w-full font-mono" data-testid="invoice-number-input" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Project *</label>
              <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value, purchase_order_id: ''})} className="input-field w-full" required data-testid="inv-project-select">
                <option value="">Select</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
              </select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Against PO</label>
              <select value={form.purchase_order_id} onChange={e => setForm({...form, purchase_order_id: e.target.value})} className="input-field w-full" data-testid="inv-po-select">
                <option value="">None</option>
                {projectPOs.map(po => <option key={po.id} value={po.id}>{po.po_number} ({po.po_type})</option>)}
              </select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Invoice Type</label>
              <select value={form.invoice_type} onChange={e => setForm({...form, invoice_type: e.target.value})} className="input-field w-full" data-testid="inv-type-select">
                {INVOICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Invoice Date</label>
              <input type="date" value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">TDS Deducted</label>
              <input type="number" value={form.tds_deducted} onChange={e => setForm({...form, tds_deducted: e.target.value})} className="input-field w-full" /></div>
          </div>

          <div className="border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Line Items (BOQ linked)</p>
              <button type="button" onClick={addLineItem} className="text-xs text-[#1B5486] hover:underline">+ Add Item</button>
            </div>
            {lineItems.map((li, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                <div className="col-span-4">
                  {idx === 0 && <label className="block text-[10px] text-slate-500 mb-0.5">BOQ Item</label>}
                  <select value={li.boq_item_id} onChange={e => updateLineItem(idx, 'boq_item_id', e.target.value)} className="input-field w-full text-xs" data-testid={`line-boq-${idx}`}>
                    <option value="">Manual entry</option>
                    {boqOptions.map(b => <option key={b.id} value={b.id}>{b.sl_no ? `${b.sl_no}. ` : ''}{b.description || b.item_name || 'Unnamed'} ({b.quoted_qty || b.units || 0} {b.unit || b.uom || 'Nos'})</option>)}
                  </select>
                </div>
                <div className="col-span-3">
                  {idx === 0 && <label className="block text-[10px] text-slate-500 mb-0.5">Description</label>}
                  <input value={li.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" className="input-field w-full text-xs" />
                </div>
                <div className="col-span-2">
                  {idx === 0 && <label className="block text-[10px] text-slate-500 mb-0.5">Qty</label>}
                  <input type="number" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} placeholder="0" className="input-field w-full text-xs" />
                </div>
                <div className="col-span-2">
                  {idx === 0 && <label className="block text-[10px] text-slate-500 mb-0.5">Unit Price</label>}
                  <input type="number" value={li.unit_price} onChange={e => updateLineItem(idx, 'unit_price', e.target.value)} placeholder="0" className="input-field w-full text-xs" />
                </div>
                <div className="col-span-1 flex items-center justify-center">
                  {lineItems.length > 1 && <button type="button" onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-600 text-xs">X</button>}
                </div>
              </div>
            ))}
            <div className="text-right text-xs text-slate-600 mt-1 font-mono">Line Total: {formatINRFull(lineTotal)}</div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Base Amount</label>
              <input type="number" value={form.base_amount} onChange={e => setForm({...form, base_amount: e.target.value})} placeholder={lineTotal.toString()} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">GST Amount</label>
              <input type="number" value={form.gst_amount} onChange={e => setForm({...form, gst_amount: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Total</label>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm font-mono">{formatINRFull(totalAmt)}</div></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Net Receivable</label>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm font-mono text-emerald-700 font-medium">{formatINRFull(netReceivable)}</div></div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-invoice-btn" className="flex-1 bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
              {saving ? 'Creating...' : 'Create Invoice'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPOModal({ po, onClose, onSaved }) {
  const [form, setForm] = useState({ ...po });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/po-invoices/purchase-orders/${po.id}`, {
        status: form.status, po_value: parseFloat(form.po_value || 0),
        gst_percent: parseFloat(form.gst_percent || 0), vendor_name: form.vendor_name, remarks: form.remarks
      });
      onSaved();
    } catch (err) { alert('Save failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="edit-po-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Edit PO - {po.po_number}</h2>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input-field w-full">
                {PO_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Vendor</label>
              <input value={form.vendor_name || ''} onChange={e => setForm({...form, vendor_name: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">PO Value</label>
              <input type="number" value={form.po_value || ''} onChange={e => setForm({...form, po_value: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">GST %</label>
              <select value={form.gst_percent} onChange={e => setForm({...form, gst_percent: e.target.value})} className="input-field w-full">
                {[0, 5, 12, 18, 28].map(g => <option key={g} value={g}>{g}%</option>)}</select></div>
          </div>
          <div><label className="block text-xs font-medium text-slate-700 mb-1">Remarks</label>
            <textarea value={form.remarks || ''} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium">
              {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditInvoiceModal({ invoice, purchaseOrders, projects, onClose, onSaved }) {
  const [form, setForm] = useState({ ...invoice });
  const [saving, setSaving] = useState(false);

  const canTransition = (current, next) => {
    const flow = { DRAFT: ['SUBMITTED'], SUBMITTED: ['ACKNOWLEDGED'], ACKNOWLEDGED: ['PAYMENT_RECEIVED'], PAYMENT_RECEIVED: [] };
    return (flow[current] || []).includes(next) || next === current;
  };

  const nextStatuses = () => {
    const flow = { DRAFT: ['DRAFT', 'SUBMITTED'], SUBMITTED: ['SUBMITTED', 'ACKNOWLEDGED'], ACKNOWLEDGED: ['ACKNOWLEDGED', 'PAYMENT_RECEIVED'], PAYMENT_RECEIVED: ['PAYMENT_RECEIVED'] };
    return flow[invoice.status] || [invoice.status];
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        status: form.status,
        tds_deducted: parseFloat(form.tds_deducted || 0),
        payment_amount: parseFloat(form.payment_amount || 0),
        payment_date: form.payment_date || '',
        notes: form.notes || ''
      };
      if (form.status === 'PAYMENT_RECEIVED' && !form.payment_date) {
        payload.payment_date = new Date().toISOString().slice(0, 10);
      }
      await api.patch(`/po-invoices/invoices/${invoice.id}`, payload);
      onSaved();
    } catch (err) { alert('Save failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="edit-invoice-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Edit Invoice - {invoice.invoice_number}</h2>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Total Amount</span><span className="font-mono font-medium">{formatINRFull(invoice.total_amount)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Current Status</span><InvoiceStatusBadge status={invoice.status} /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input-field w-full" data-testid="edit-inv-status">
                {nextStatuses().map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-slate-700 mb-1">TDS Deducted</label>
              <input type="number" value={form.tds_deducted || ''} onChange={e => setForm({...form, tds_deducted: e.target.value})} className="input-field w-full" data-testid="edit-inv-tds" /></div>
          </div>

          {(form.status === 'PAYMENT_RECEIVED' || invoice.status === 'PAYMENT_RECEIVED') && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Payment Amount</label>
                <input type="number" value={form.payment_amount || ''} onChange={e => setForm({...form, payment_amount: e.target.value})} className="input-field w-full" data-testid="edit-inv-payment-amt" /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">Payment Date</label>
                <input type="date" value={form.payment_date || ''} onChange={e => setForm({...form, payment_date: e.target.value})} className="input-field w-full" /></div>
            </div>
          )}

          <div><label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
            <textarea value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="input-field w-full" /></div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="flex-1 bg-emerald-600 text-white py-2 rounded-md text-sm font-medium" data-testid="save-invoice-btn">
              {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
